import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService.js';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startLoading, setStartLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const response = await projectService.fetchDashboard();
      const projects = response.projects || [];
      const currentProject = projects.find(p => p.project_id === parseInt(projectId));
      
      if (currentProject) {
        setProject(currentProject);
      } else {
        setError('Project not found');
      }
    } catch (error) {
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const handleStartProject = async () => {
    setStartLoading(true);
    setError('');

    try {
      const response = await projectService.startProject(project.project_id);
      const updatedProject = {
        ...project,
        container_status: 'running',
        container_url: response.container_url,
        port_url: response.port_urls || [],
        node_url: response.node_url || '',
        updated_at: new Date().toISOString()
      };
      setProject(updatedProject);
    } catch (error) {
      setError(error.error || error.message || 'Failed to start project');
    } finally {
      setStartLoading(false);
    }
  };

  // Delete Project modal state and handler
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const handleDeleteProject = async () => {
    setDeleteError("");
    if (deleteInput !== project.project_name) {
      setDeleteError("Project name does not match. Please type the exact project name to confirm deletion.");
      return;
    }
    setDeleteLoading(true);
    setError("");
    try {
      await projectService.deleteProject(project.project_id);
      setShowDeleteModal(false);
      navigate('/dashboard');
    } catch (error) {
      setDeleteError(error.error || error.message || 'Failed to delete project');
    } finally {
      setDeleteLoading(false);
      setDeleteInput("");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="project-details-page">
        <div className="loading-container-modern">
          <div className="loading-spinner-modern">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <p className="loading-text-modern">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-details-page">
        <div className="error-container-modern">
          <div className="error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="error-content">
            <h3 className="error-title">Project Not Found</h3>
            <p className="error-message">{error || 'The requested project could not be found.'}</p>
            <button onClick={() => navigate('/dashboard')} className="error-back-btn">
              <i className="fas fa-arrow-left"></i>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-page">
      <div className="project-details-container-modern">
        {/* Header Section */}
        <div className="project-header-modern">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="back-btn-modern"
            title="Back to Dashboard"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          
          <div className="project-title-area">
            <div className="project-icon-modern">
              <i className="fas fa-layer-group"></i>
            </div>
            <div className="project-info-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <h1 className="project-title-modern">{project.project_name}</h1>
                {project.container_status === 'running' && project.container_url && (
                  <button
                    className="url-link-modern url-btn project-url-btn"
                    style={{ marginLeft: '0.5rem', padding: '0.5rem 1rem', fontSize: '1rem', background: 'var(--primary-color)', color: '#fff', borderRadius: '6px', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    onClick={() => window.open(project.container_url.startsWith('http') ? project.container_url : `http://${project.container_url}`, '_blank', 'noopener,noreferrer')}
                  >
                    Project URL
                    <i className="fas fa-external-link-alt"></i>
                  </button>
                )}
                <div className="project-meta">
                  <span className="project-id">#{project.project_id}</span>
                  <div className={`status-badge-modern ${project.container_status === 'running' ? 'status-running' : 'status-stopped'}`}>
                    <div className="status-dot"></div>
                    <span>{project.container_status === 'running' ? 'Running' : 'Stopped'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert-modern error">
            <div className="alert-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <span>{error}</span>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="project-content-grid">
          {/* Project Information Card */}
          <div className="info-card-modern">
            <div className="card-header-modern">
              <div className="header-icon">
                <i className="fas fa-info-circle"></i>
              </div>
              <h3 className="card-title">Project Information</h3>
            </div>
            <div className="card-content-modern">
              <div className="info-grid">
                <div className="info-item-modern">
                <div className="info-icon" style={{ color: 'var(--primary-color)' }}>
                  <i className="fas fa-hashtag"></i>
                </div>
                  <div className="info-details">
                    <span className="info-label">Project ID</span>
                    <span className="info-value">{project.project_id}</span>
                  </div>
                </div>
                
                <div className="info-item-modern">
                <div className="info-icon" style={{ color: 'var(--primary-color)' }}>
                  <i className="fas fa-server"></i>
                </div>
                  <div className="info-details">
                    <span className="info-label">Host ID</span>
                    <span className="info-value">{project.host_id}</span>
                  </div>
                </div>
                
                <div className="info-item-modern">
                <div className="info-icon" style={{ color: 'var(--primary-color)' }}>
                  <i className="fas fa-calendar-plus"></i>
                </div>
                  <div className="info-details">
                    <span className="info-label">Created</span>
                    <span className="info-value">{formatDate(project.created_at)}</span>
                  </div>
                </div>
                
                <div className="info-item-modern">
                <div className="info-icon" style={{ color: 'var(--primary-color)' }}>
                  <i className="fas fa-clock"></i>
                </div>
                  <div className="info-details">
                    <span className="info-label">Last Updated</span>
                    <span className="info-value">{formatDate(project.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Collaborators Card */}
          <div className="info-card-modern">
            <div className="card-header-modern">
              <div className="header-icon" style={{ color: 'var(--primary-color)' }}>
                <i className="fas fa-users"></i>
              </div>
              <h3 className="card-title">Collaborators</h3>
            </div>
            <div className="card-content-modern">
              {project.collaborators && project.collaborators.length > 0 ? (
                <div className="collaborators-grid">
                  {project.collaborators.map((email, index) => (
                    <div key={index} className="collaborator-item-modern">
                      <div className="collaborator-avatar" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                        {email.charAt(0).toUpperCase()}
                      </div>
                      <div className="collaborator-info">
                        <span className="collaborator-email">{email}</span>
                        <span className="collaborator-role">Collaborator</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-small">
                  <div className="empty-icon" style={{ color: 'var(--primary-color)' }}>
                    <i className="fas fa-user-plus"></i>
                  </div>
                  <div className="empty-text">
                    <p>No collaborators yet</p>
                    <span>Add team members to collaborate</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="info-card-modern actions-card-modern">
            <div className="card-header-modern">
              <div className="header-icon" style={{ color: 'var(--primary-color)' }}>
                <i className="fas fa-bolt"></i>
              </div>
              <h3 className="card-title">Quick Actions</h3>
            </div>
            <div className="card-content-modern">
          <div className="actions-grid-modern">
            {project.container_status !== 'running' && (
              <button 
                onClick={handleStartProject}
                disabled={startLoading}
                className="action-btn-modern primary"
              >
                <div className="btn-icon" style={{ color: '#fff' }}>
                  <i className={startLoading ? "fas fa-spinner fa-spin" : "fas fa-play"}></i>
                </div>
                <div className="btn-content">
                  <span className="btn-title">{startLoading ? 'Starting...' : 'Start Project'}</span>
                  <span className="btn-subtitle">Launch development environment</span>
                </div>
              </button>
            )}

            <a 
              href={`/edit-collaborators/${project.project_id}`}
              className="action-btn-modern secondary"
            >
              <div className="btn-icon" style={{ color: 'var(--primary-color)' }}>
                <i className="fas fa-users"></i>
              </div>
              <div className="btn-content">
                <span className="btn-title">Manage Team</span>
                <span className="btn-subtitle">Add or remove collaborators</span>
              </div>
            </a>

            <a 
              href={`/update-project-password/${project.project_id}`}
              className="action-btn-modern warning"
            >
              <div className="btn-icon" style={{ color: 'var(--primary-color)' }}>
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="btn-content">
                <span className="btn-title">Security</span>
                <span className="btn-subtitle">Update project password</span>
              </div>
            </a>

            {/* Delete Project Button */}
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={deleteLoading}
              className="action-btn-modern delete-project-btn-creative"
            >
              <div className="btn-background-effect"></div>
              <div className="btn-icon-creative">
                <div className="icon-ring"></div>
                <i className={deleteLoading ? "fas fa-spinner fa-spin" : "fas fa-trash-alt"}></i>
              </div>
              <div className="btn-content">
                <span className="btn-title">{deleteLoading ? 'Deleting...' : 'Delete Project'}</span>
                <span className="btn-subtitle">Permanently remove this project</span>
              </div>
              <div className="danger-glow-creative"></div>
            </button>
          </div>
            </div>
          </div>

          {/* Access URLs Card */}
          {project.container_status === 'running' && (project.container_url || project.port_url?.length > 0 || project.node_url) && (
            <div className="info-card-modern urls-card-modern">
          
              <div className="card-content-modern">
                {/* Node URL Section */}
                {project.node_url && (
                  <div className="url-section-redesigned">
                    <div className="section-header-redesigned">
                      <div className="section-icon-redesigned node">
                        <i className="fab fa-node-js"></i>
                      </div>
                      <div className="header-content">
                        <h4>Main Application</h4>
                        <span className="primary-badge">Primary</span>
                      </div>
                    </div>
                    
                    <div className="main-app-container">
                      <div className="main-app-card">
                        <button
                          className="main-app-button"
                          onClick={() => window.open(project.node_url.startsWith('http') ? project.node_url : `http://${project.node_url}`, '_blank', 'noopener,noreferrer')}
                        >
                          <div className="main-app-left">
                            <div className="main-app-icon">
                              <div className="icon-glow"></div>
                              <i className="fab fa-node-js"></i>
                            </div>
                            <div className="main-app-info">
                              <div className="app-title">Node.js Application</div>
                              <div className="app-description">Primary development server</div>
                              <div className="app-url">{project.node_url}</div>
                            </div>
                          </div>
                          
                          <div className="main-app-right">
                            <div className="main-launch-btn">
                              <span>Open App</span>
                              <i className="fas fa-external-link-alt"></i>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Port URLs Section */}
                {project.port_url && project.port_url.length > 0 && (
                  <div className="url-section-redesigned">
                    <div className="section-header-redesigned">
                      <div className="section-icon-redesigned">
                        <i className="fas fa-network-wired"></i>
                      </div>
                      <div className="header-content">
                        <h4>Development Ports</h4>
                        <div className="running-indicator">
                           <div className="pulse-dot"></div>
                           <span>Live</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ports-container-redesigned">
                      {project.port_url.map((url, index) => {
                        // Extract port number from the URL
                        let port = '';
                        
                        try {
                          const urlObj = new URL(url.startsWith('http') ? url : `http://${url}`);
                          port = urlObj.port || (urlObj.protocol === 'https:' ? '443' : '80');
                          
                          // If no port in URL, try to extract from path
                          if (!urlObj.port && urlObj.pathname !== '/') {
                            const pathSegments = urlObj.pathname.split('/').filter(Boolean);
                            const lastSegment = pathSegments[pathSegments.length - 1];
                            if (/^\d+$/.test(lastSegment)) {
                              port = lastSegment;
                            }
                          }
                        } catch {
                          // Fallback: try to extract port from string
                          const portMatch = url.match(/:(\d+)/);
                          port = portMatch ? portMatch[1] : `${3000 + index}`;
                        }

                        return (
                          <div key={index} className="port-card-redesigned">
                            <button
                              className="port-button-redesigned"
                              onClick={() => window.open(url.startsWith('http') ? url : `http://${url}`, '_blank', 'noopener,noreferrer')}
                            >
                              <div className="port-btn-left">
                                <div className="port-icon-wrapper">
                                  <div className="port-icon-bg"></div>
                                  <i className="fas fa-globe"></i>
                                </div>
                                <div className="port-info">
                                  <div className="port-number">Port {port}</div>
                                </div>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Project Modal - Positioned at page level for center positioning */}
      {showDeleteModal && (
        <div className="delete-modal-overlay-creative">
          <div className="delete-modal-backdrop-creative" onClick={() => { setShowDeleteModal(false); setDeleteInput(""); setDeleteError(""); }}></div>
          <div className="delete-modal-container-creative">
            <div className="delete-modal-content-creative">
              {/* Modal Header */}
              <div className="delete-modal-header-creative">
                <div className="warning-icon-container-creative">
                  <div className="warning-pulse-creative"></div>
                  <div className="warning-rings-creative">
                    <div className="ring ring-1"></div>
                    <div className="ring ring-2"></div>
                    <div className="ring ring-3"></div>
                  </div>
                  <i className="fas fa-exclamation-triangle warning-icon-creative"></i>
                </div>
                <h2 className="delete-modal-title-creative">Delete Project</h2>
                <p className="delete-modal-subtitle-creative">This action cannot be undone</p>
              </div>

              {/* Warning Message */}
              <div className="delete-warning-section-creative">
                <div className="warning-card-creative">
                  <div className="warning-icon-small-creative">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className="warning-content-creative">
                    <h4>Data Loss Warning</h4>
                    <p>All project data will be permanently deleted from our system and cannot be recovered.</p>
                  </div>
                </div>
                
                <div className="recommendation-card-creative">
                  <div className="recommendation-icon-creative">
                    <i className="fab fa-github"></i>
                  </div>
                  <div className="recommendation-content-creative">
                    <h4>Recommended Action</h4>
                    <p>Make sure you push your project to GitHub before deleting it.</p>
                  </div>
                </div>
              </div>

              {/* Confirmation Input */}
              <div className="delete-confirmation-section-creative">
                <label className="confirmation-label-creative">
                  Type <strong>{project.project_name}</strong> to confirm deletion:
                </label>
                <div className="input-container-creative">
                  <input
                    type="text"
                    value={deleteInput}
                    onChange={e => setDeleteInput(e.target.value)}
                    placeholder="Enter project name"
                    className="confirmation-input-creative"
                    autoFocus
                  />
                  <div className={`input-validation-creative ${deleteInput === project.project_name ? 'valid' : 'invalid'}`}>
                    <i className={`fas ${deleteInput === project.project_name ? 'fa-check' : 'fa-times'}`}></i>
                  </div>
                </div>
                {deleteError && (
                  <div className="delete-error-message-creative">
                    <i className="fas fa-exclamation-circle"></i>
                    {deleteError}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="delete-modal-actions-creative">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteInput(""); setDeleteError(""); }}
                  disabled={deleteLoading}
                  className="cancel-btn-creative"
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  disabled={deleteLoading || deleteInput !== project.project_name}
                  className={`delete-confirm-btn-creative ${deleteInput === project.project_name && !deleteLoading ? 'enabled' : 'disabled'}`}
                >
                  <div className="btn-background-danger-creative"></div>
                  <div className="btn-icon-danger">
                    <i className={deleteLoading ? "fas fa-spinner fa-spin" : "fas fa-trash-alt"}></i>
                  </div>
                  <span>{deleteLoading ? 'Deleting...' : 'Delete Forever'}</span>
                  <div className="btn-danger-glow"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
